import { useCallback, useEffect, lazy, Suspense, useRef, startTransition, useState } from 'react'
import { QuickView } from '~/app/features/QuickView'
import { useDispatch, useSelector } from 'react-redux'
import { getQuickViewId } from '~/app/store/debug'
import { resetQuickViewId, setIsSimilarProduct } from '~/app/store/debug/actions'
import { getDebugModeEnabled, getIsDirectQuickView, getSimilarProduct, getTakeOverQV } from '~/app/store/debug/reducer'
import { GalleryQuickViewSpinner } from '~/app/features/Gallery/components/PreviewArea/GalleryQuickView/GalleryQuickViewSpinner'
import { resetSimilarTemplates } from '~/app/store/similarTemplates/action'
import { ThunkDispatch } from '~/app/store/interface'
import { useValidQuickViewEntity } from '~/app/features/Gallery/components/PreviewArea/GalleryQuickView/hooks/useValidQuickViewEntity'
import { trackDirectQVDismissImpression } from '~/app/store/analytics/thunks'
import { useAnalytics } from '~/app/features/Gallery/hooks/useAnalytics'
import { useLogger } from '@vp/ubik-context'
import { LoggingErrorBoundary } from '~/app/features/Gallery/components/ErrorBoundary'
import { useFeatureGate } from '~/app/features/Gallery/hooks/useFeatureGate'
import { FEATURE_GATES } from 'shared/featureGates/constants'
import { useNewQuickViewProps } from '~/app/features/Gallery/components/PreviewArea/hooks/useNewQuickViewProps'
import { refinementIdsByFilterSelector } from '~/app/store/refinement'
import { getContentCategories } from '~/app/store/content/actions'
import { getQuantityDataSelector } from '~/app/store/config/reducer'
import { useNewQuickViewAnalyticsCallbacks } from '~/app/features/Gallery/components/PreviewArea/hooks/useNewQuickViewAnalyticsCallbacks'

const GalleryQuickView = lazy(() => import('~/app/features/Gallery/components/PreviewArea/GalleryQuickView'))
const NewQuickView = lazy(() => import('@vp/quick-view-modal').then((module) => ({ default: module.QuickView })))

export const QuickViewModal = () => {
  const logger = useLogger()
  const dispatch = useDispatch<ThunkDispatch>()
  const isTakeOverQV = useSelector(getTakeOverQV)
  const designId = useSelector(getQuickViewId)
  const isDirectQuickView = useSelector(getIsDirectQuickView)
  const isSimilarProduct = !!useSelector(getSimilarProduct)
  const quantity = useSelector(getQuantityDataSelector)
  const debugEnabled = useSelector(getDebugModeEnabled)
  const analytics = useAnalytics()
  const newQuickViewModalFeatureGate = useFeatureGate(FEATURE_GATES.GALLERY_QUICK_VIEW_MODAL_ENABLED)
  const refinements = useSelector(refinementIdsByFilterSelector)
  const { categories, facetCategories } = useSelector(getContentCategories)(refinements.categories)
  const categoriesForQV = categories.flat()

  const tileEntity = useValidQuickViewEntity(designId)
  const {
    selectedDesignConceptId,
    mpvId,
    productQuantity,
    locale,
    designPersonalization,
    selectedOptions,
    taxonomyFilters,
    designCreationTypes,
    previouslyOrderedDate,
    enableZoomablePreview,
    scenePurpose,
    hideColorSwatches,
    renderPropertyOverrides,
  } = useNewQuickViewProps(tileEntity)

  const analyticsCallbacks = useNewQuickViewAnalyticsCallbacks(tileEntity, productQuantity)

  const handleOnRequestDismiss = useCallback((): void => {
    if (isTakeOverQV && !newQuickViewModalFeatureGate) {
      // In urlQueryParam mode the package already navigated history via history.go(-depth);
      // a second window.history.back() would over-navigate past the pre-modal URL.
      window.history.back()
      return
    }

    // Defer actions dispatching to hide modal immediately
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        startTransition(() => {
          if (isDirectQuickView && designId) {
            dispatch(trackDirectQVDismissImpression(designId, analytics))
          }

          if (isSimilarProduct) {
            dispatch(setIsSimilarProduct(false))
          }

          // Omit designId on the new path: the tileEntity reducer early-returns without
          // creating new byId references, avoiding a gallery tile re-render flash.
          dispatch(resetQuickViewId(newQuickViewModalFeatureGate ? undefined : designId))
          dispatch(resetSimilarTemplates())
        })
      })
    })
  }, [isTakeOverQV, newQuickViewModalFeatureGate, isDirectQuickView, dispatch, designId, analytics, isSimilarProduct])

  const modalRef = useRef<HTMLDialogElement>(null)
  const [shouldRenderContent, setShouldRenderContent] = useState(false)

  // Defer content rendering to show modal immediately
  useEffect(() => {
    startTransition(() => {
      setShouldRenderContent(!!tileEntity)
    })
  }, [tileEntity])

  useEffect(() => {
    modalRef.current?.scrollTo(0, 0)
  }, [designId])

  const showNewQuickViewModal = newQuickViewModalFeatureGate && !!selectedDesignConceptId && !!designId

  if (showNewQuickViewModal) {
    return (
      <NewQuickView
        urlQueryParam="qv"
        onClose={handleOnRequestDismiss}
        designConceptId={selectedDesignConceptId}
        entityId={designId}
        mpvId={mpvId}
        initialOrDefaultQuantity={productQuantity}
        locale={locale}
        designPersonalization={designPersonalization}
        selectedOptions={selectedOptions}
        taxonomyFilters={taxonomyFilters}
        designCreationTypes={designCreationTypes}
        previouslyOrderedDate={previouslyOrderedDate}
        enableZoomablePreview={enableZoomablePreview}
        scenePurpose={scenePurpose}
        hideColorSwatches={hideColorSwatches}
        renderPropertyOverrides={renderPropertyOverrides}
        debugEnabled={debugEnabled}
        showMoreLikeThis
        categories={categoriesForQV}
        facetCategories={facetCategories}
        {...analyticsCallbacks}
        {...(quantity && { quantity })}
      />
    )
  }

  return (
    <QuickView
      onlyRenderWhenOpen
      designId={designId}
      id={`quickview-${designId}`}
      isOpen={!!tileEntity}
      ref={modalRef}
      onRequestDismiss={handleOnRequestDismiss}
    >
      <LoggingErrorBoundary logger={logger}>
        {shouldRenderContent && <Suspense fallback={<GalleryQuickViewSpinner />}>{tileEntity && <GalleryQuickView tileEntity={tileEntity} />}</Suspense>}
        {!shouldRenderContent && <GalleryQuickViewSpinner />}
      </LoggingErrorBoundary>
    </QuickView>
  )
}
